import { ref } from 'vue'
import { useMessage } from '@/mini/composables/useMessage'
import { normalizeApiPrice, normalizeSetType, getCardApiBaseBySetType, isFresh, bestMatchByName } from '@/utils/cardFormUtils'

const OPTCG_API_BASE_URL = import.meta.env.VITE_OPTCG_API_BASE_URL
const RATE_LIMIT_KEY = 'optcg.rateLimit.optcgApi'
const RATE_LIMIT_MAX = 500
const RATE_LIMIT_WINDOW_MS = 30 * 60 * 1000

function getRateLimit() {
  try {
    const stored = JSON.parse(localStorage.getItem(RATE_LIMIT_KEY))
    if (!stored || typeof stored.count !== 'number' || !stored.windowStart) return null
    return stored
  } catch { return null }
}

function setRateLimit(count, windowStart) {
  try { localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify({ count, windowStart })) } catch {}
}

export function useMarketPrices(authenticatedApiFetch) {
  const { showInfoMessage, showSuccessMessage, showDangerMessage, showPersistentMessage, updateMessage, dismissMessage } = useMessage()

  const isFetchingAllCards = ref(false)
  const fetchAllCardsProgress = ref({ done: 0, total: 0, errors: 0 })

  async function fetchMarketPricesForCards(source, force = false) {
    if (isFetchingAllCards.value) return

    const eligible = source.filter(card => {
      const setType = normalizeSetType(card.set_type, card.card_id)
      return card.card_id && getCardApiBaseBySetType(setType) && (force || !isFresh(card))
    })

    if (!eligible.length) {
      showInfoMessage('All prices are already up to date.')
      return
    }

    // Rate limit: max 500 cards per 30-minute window (external API constraint)
    const now = Date.now()
    let rl = getRateLimit()
    if (!rl || (now - new Date(rl.windowStart).getTime()) >= RATE_LIMIT_WINDOW_MS) {
      rl = { count: 0, windowStart: new Date(now).toISOString() }
    }

    const budget = RATE_LIMIT_MAX - rl.count
    if (budget <= 0) {
      const minsLeft = Math.ceil((RATE_LIMIT_WINDOW_MS - (now - new Date(rl.windowStart).getTime())) / 60000)
      showDangerMessage(`Rate limit reached (500 cards / 30 min). Try again in ${minsLeft} min.`)
      return
    }

    const toFetch = budget < eligible.length ? eligible.slice(0, budget) : eligible
    if (budget < eligible.length) {
      const minsLeft = Math.ceil((RATE_LIMIT_WINDOW_MS - (now - new Date(rl.windowStart).getTime())) / 60000)
      showInfoMessage(`Rate limit: fetching ${budget} of ${eligible.length} — ${eligible.length - budget} skipped, try again in ~${minsLeft} min.`)
    }

    isFetchingAllCards.value = true
    fetchAllCardsProgress.value = { done: 0, total: toFetch.length, errors: 0 }
    const progressId = showPersistentMessage(`Fetching prices… 0 / ${toFetch.length}`, 'info')

    for (const card of toFetch) {
      const setType = normalizeSetType(card.set_type, card.card_id)
      const base = getCardApiBaseBySetType(setType)
      try {
        const response = await fetch(`${OPTCG_API_BASE_URL}/${base}/card/${encodeURIComponent(card.card_id)}/`)
        if (!response.ok) throw new Error('HTTP ' + response.status)
        const data = await response.json()
        const cardCandidates = (Array.isArray(data) ? data : [data]).filter(item => item && typeof item === 'object')
        if (!cardCandidates.length) throw new Error('Empty response')
        const candidate = cardCandidates.length > 1 ? bestMatchByName(card.card_name, cardCandidates) : cardCandidates[0]
        const marketPrice = normalizeApiPrice(candidate.market_price ?? candidate.marketPrice ?? candidate.price)
        if (marketPrice === '') throw new Error('No price')
        const now = new Date().toISOString()

        await authenticatedApiFetch(`/api/v1/cards/${card.id}/optcg/`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            market_price: marketPrice,
            fetched_timestamp: now,
          })
        })
        card.market_price = marketPrice
        card.fetched_timestamp = now
      } catch {
        fetchAllCardsProgress.value.errors++
        showDangerMessage(`${card.card_id} – ${card.card_name}: price fetch failed`)
      }
      fetchAllCardsProgress.value.done++
      const { done, total, errors } = fetchAllCardsProgress.value
      updateMessage(progressId, `Fetching prices… ${done} / ${total}${errors > 0 ? ` (${errors} error(s))` : ''}`)
    }

    setRateLimit(rl.count + toFetch.length, rl.windowStart)
    dismissMessage(progressId)
    const { done, total, errors } = fetchAllCardsProgress.value
    const doneMsg = `Prices updated: ${done}/${total}${errors > 0 ? ` (${errors} error(s))` : ''}`
    errors > 0 ? showDangerMessage(doneMsg) : showSuccessMessage(doneMsg)
    isFetchingAllCards.value = false
  }

  return {
    isFetchingAllCards,
    fetchAllCardsProgress,
    fetchMarketPricesForCards,
  }
}
